import React, { useState, useEffect } from 'react';
import { testCC4Parser, createFileFromPath } from '../utils/testCC4Parser';
import CC4FileUpload from '../components/reports/CC4FileUpload';

const TestCC4ParserPage = () => {
  const [parsedData, setParsedData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Handle successful upload from CC4FileUpload component
  const handleUploadSuccess = (data) => {
    console.log('Upload success:', data);
    setParsedData(data);
  };

  // Test with sample file
  const testWithSampleFile = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Create a File object from the sample CC4 file
      const file = await createFileFromPath('/VO2CR-1060-000021ParekhRitesh.cc4');
      
      // Test the parser
      const data = await testCC4Parser(file);
      setParsedData(data);
    } catch (err) {
      console.error('Error testing parser:', err);
      setError(err.message || 'Failed to test parser');
    } finally {
      setIsLoading(false);
    }
  };

  // Format object for display
  const formatObject = (obj, indent = 0) => {
    if (!obj) return 'null';
    
    if (typeof obj !== 'object') {
      return String(obj);
    }
    
    if (Array.isArray(obj)) {
      if (obj.length === 0) return '[]';
      return `Array(${obj.length}) [${obj.length > 5 ? ' ... ' : obj.map(item => formatObject(item)).join(', ')}]`;
    }
    
    const keys = Object.keys(obj);
    if (keys.length === 0) return '{}';
    
    const indentStr = ' '.repeat(indent);
    const innerIndentStr = ' '.repeat(indent + 2);
    
    return `{\n${keys.map(key => `${innerIndentStr}${key}: ${formatObject(obj[key], indent + 2)}`).join(',\n')}\n${indentStr}}`;
  };

  return (
    <div className="container mx-auto p-4">
      <h1 className="text-2xl font-bold mb-4">CC4 Parser Test Page</h1>
      
      <div className="mb-6">
        <button 
          onClick={testWithSampleFile}
          disabled={isLoading}
          className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded mr-2"
        >
          {isLoading ? 'Testing...' : 'Test with Sample File'}
        </button>
      </div>
      
      <div className="mb-6">
        <h2 className="text-xl font-semibold mb-2">Upload CC4 File</h2>
        <CC4FileUpload onUploadSuccess={handleUploadSuccess} />
      </div>
      
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          <strong>Error:</strong> {error}
        </div>
      )}
      
      {parsedData && (
        <div className="mt-6">
          <h2 className="text-xl font-semibold mb-2">Parsed Data</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-gray-50 p-4 rounded shadow">
              <h3 className="font-medium mb-2">Patient Info</h3>
              <pre className="text-sm bg-gray-100 p-2 rounded overflow-auto max-h-40">
                {formatObject(parsedData.patientInfo)}
              </pre>
            </div>
            
            <div className="bg-gray-50 p-4 rounded shadow">
              <h3 className="font-medium mb-2">VO2 Max Data</h3>
              <div className="grid grid-cols-2 gap-2">
                <div>VO2 Max:</div>
                <div>{parsedData.vo2Max} {parsedData.vo2MaxUnit}</div>
                
                <div>Heart Rate Max:</div>
                <div>{parsedData.heartRateMax} bpm</div>
                
                <div>Peak Heart Rate:</div>
                <div>{parsedData.peakHeartRate} bpm</div>
                
                <div>Heart Rate Rest:</div>
                <div>{parsedData.heartRateRest} bpm</div>
                
                <div>Fitness Level:</div>
                <div>{parsedData.fitnessLevel}</div>
                
                <div>Anaerobic Threshold:</div>
                <div>{parsedData.anaerobicThreshold} {parsedData.vo2MaxUnit}</div>
                
                <div>Anaerobic Threshold HR:</div>
                <div>{parsedData.anaerobicThresholdHR} bpm</div>
                
                <div>Predicted VO2 Max:</div>
                <div>{parsedData.predictedVO2Max} {parsedData.vo2MaxUnit}</div>
              </div>
            </div>
            
            <div className="bg-gray-50 p-4 rounded shadow">
              <h3 className="font-medium mb-2">Test Information</h3>
              <div className="grid grid-cols-2 gap-2">
                <div>Test Date:</div>
                <div>{parsedData.testDate}</div>
                
                <div>Test ID:</div>
                <div>{parsedData.testId}</div>
                
                <div>Report ID:</div>
                <div>{parsedData.reportId}</div>
              </div>
            </div>
            
            <div className="bg-gray-50 p-4 rounded shadow">
              <h3 className="font-medium mb-2">Recovery Data</h3>
              <div className="grid grid-cols-1 gap-2">
                <div>
                  <h4 className="text-sm font-medium">Recovery Heart Rates:</h4>
                  <pre className="text-sm bg-gray-100 p-2 rounded overflow-auto max-h-40">
                    {formatObject(parsedData.recoveryHeartRates)}
                  </pre>
                </div>
                
                <div>
                  <h4 className="text-sm font-medium mt-2">Recovery VO2 Values:</h4>
                  <pre className="text-sm bg-gray-100 p-2 rounded overflow-auto max-h-40">
                    {formatObject(parsedData.recoveryVO2Values)}
                  </pre>
                </div>
                
                <div>
                  <h4 className="text-sm font-medium mt-2">Recovery Indices:</h4>
                  <pre className="text-sm bg-gray-100 p-2 rounded overflow-auto max-h-40">
                    {formatObject(parsedData.recoveryIndices)}
                  </pre>
                </div>
              </div>
            </div>
            
            <div className="bg-gray-50 p-4 rounded shadow">
              <h3 className="font-medium mb-2">Heart Rate Zones</h3>
              <pre className="text-sm bg-gray-100 p-2 rounded overflow-auto max-h-40">
                {formatObject(parsedData.heartRateZones)}
              </pre>
            </div>
            
            <div className="bg-gray-50 p-4 rounded shadow">
              <h3 className="font-medium mb-2">Data Samples</h3>
              <div>Total Samples: {parsedData.dataSamples?.length || 0}</div>
              {parsedData.dataSamples?.length > 0 && (
                <>
                  <h4 className="text-sm font-medium mt-2">First Sample:</h4>
                  <pre className="text-sm bg-gray-100 p-2 rounded overflow-auto max-h-40">
                    {formatObject(parsedData.dataSamples[0])}
                  </pre>
                  
                  <h4 className="text-sm font-medium mt-2">Last Sample:</h4>
                  <pre className="text-sm bg-gray-100 p-2 rounded overflow-auto max-h-40">
                    {formatObject(parsedData.dataSamples[parsedData.dataSamples.length - 1])}
                  </pre>
                </>
              )}
            </div>
            <div className="bg-gray-50 p-4 rounded shadow">
              <h3 className="font-medium mb-2">Stage Data</h3>
              <div>Total Stages: {Object.keys(parsedData.stageData || {}).length}</div>
              {Object.keys(parsedData.stageData || {}).length > 0 && (
                <div className="mt-2">
                  <h4 className="text-sm font-medium">First 10 Stages:</h4>
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead>
                        <tr className="bg-gray-200">
                          <th className="px-2 py-1 text-left">Stage</th>
                          <th className="px-2 py-1 text-left">Grade</th>
                          <th className="px-2 py-1 text-left">Speed</th>
                          <th className="px-2 py-1 text-left">Time</th>
                          <th className="px-2 py-1 text-left">Duration</th>
                          <th className="px-2 py-1 text-left">Distance</th>
                          <th className="px-2 py-1 text-left">Calories</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.keys(parsedData.stageData || {})
                          .slice(0, 10)
                          .map(stageKey => {
                            const stage = parsedData.stageData[stageKey];
                            return (
                              <tr key={stageKey} className="border-b border-gray-200">
                                <td className="px-2 py-1">{stageKey}</td>
                                <td className="px-2 py-1">{stage.grade}</td>
                                <td className="px-2 py-1">{stage.speed}</td>
                                <td className="px-2 py-1">{stage.time}</td>
                                <td className="px-2 py-1">{stage.duration}</td>
                                <td className="px-2 py-1">{stage.distance}</td>
                                <td className="px-2 py-1">{stage.calories}</td>
                              </tr>
                            );
                          })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </div>
          
          <div className="mt-6">
            <h3 className="font-medium mb-2">Raw Data</h3>
            <pre className="text-xs bg-gray-100 p-2 rounded overflow-auto max-h-96">
              {JSON.stringify(parsedData, null, 2)}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
};

export default TestCC4ParserPage;
